import {
  useRef,
  useEffect,
  useCallback,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import CytoscapeComponent from 'react-cytoscapejs';
import cytoscape from 'cytoscape';
import dagre from 'cytoscape-dagre';
import cola from 'cytoscape-cola';
import fcose from 'cytoscape-fcose';
import { useGraphStore } from '@/store/graphStore';
import { threatColor } from '@/types/dns';
import type { CytoElement, DNSNode } from '@/types/dns';
import type { ThreatOverlayPoint } from '@/store/overlayDataStore';

// Register layouts once at module level (not inside render)
cytoscape.use(dagre);
cytoscape.use(cola);
cytoscape.use(fcose);

// ---------------------------------------------------------------------------
// Imperative handle — lets useScanStream call batchAdd without React state
// ---------------------------------------------------------------------------

export interface InfrastructureGraphHandle {
  /** Add elements in a Cytoscape batch, then re-run layout on new nodes only */
  batchAdd: (elements: CytoElement[]) => void;
  /** Expose the Cytoscape core instance for imperative overlay manipulation */
  getCy: () => cytoscape.Core | null;
}

// ---------------------------------------------------------------------------
// Cytoscape stylesheet
// ---------------------------------------------------------------------------

type CyStylesheet = { selector: string; style: Record<string, unknown> };
const STYLESHEET: CyStylesheet[] = [
  // --- Base node style ---
  {
    selector: 'node',
    style: {
      'background-color': ((ele: cytoscape.NodeSingular) =>
        threatColor((ele.data('threatScore') as number) ?? 0)) as unknown as string,
      'label': 'data(label)',
      'color': '#c9d1d9',
      'font-family': '"JetBrains Mono", monospace',
      'font-size': 11,
      'text-valign': 'bottom',
      'text-halign': 'center',
      'text-margin-y': 4,
      'width': 28,
      'height': 28,
      'border-width': 1.5,
      'border-color': '#30363d',
    },
  },
  // --- Node type shapes ---
  {
    selector: 'node[type = "domain"]',
    style: {
      'shape': 'round-rectangle',
      'width': 36,
      'height': 22,
      'background-color': ((ele: cytoscape.NodeSingular) =>
        threatColor((ele.data('threatScore') as number) ?? 0)) as unknown as string,
      'border-color': '#58a6ff',
      'border-width': 2,
    },
  },
  {
    selector: 'node[type = "ip"]',
    style: { 'shape': 'ellipse' },
  },
  {
    selector: 'node[type = "asn"]',
    style: {
      'shape': 'round-rectangle',
      'background-color': 'rgba(88, 166, 255, 0.05)',
      'border-color': '#58a6ff',
      'border-width': 1,
      'border-style': 'dashed',
      'text-valign': 'top',
      'text-halign': 'center',
      'font-size': 12,
      'color': '#58a6ff',
      'padding': '20px',
    },
  },
  {
    selector: 'node[type = "ns"]',
    style: {
      'shape': 'diamond',
      'background-color': ((ele: cytoscape.NodeSingular) =>
        threatColor((ele.data('threatScore') as number) ?? 0)) as unknown as string,
      'border-color': '#f0883e',
    },
  },
  {
    selector: 'node[type = "mx"]',
    style: {
      'shape': 'pentagon',
      'background-color': ((ele: cytoscape.NodeSingular) =>
        threatColor((ele.data('threatScore') as number) ?? 0)) as unknown as string,
      'border-color': '#56d364',
    },
  },
  {
    selector: 'node[type = "cname"]',
    style: {
      'shape': 'triangle',
      'background-color': ((ele: cytoscape.NodeSingular) =>
        threatColor((ele.data('threatScore') as number) ?? 0)) as unknown as string,
      'border-color': '#8b949e',
    },
  },
  // --- Selected node highlight ---
  {
    selector: 'node:selected',
    style: {
      'border-width': 3,
      'border-color': '#58a6ff',
      'background-opacity': 0.9,
    },
  },
  // --- Flagged by overlay — visual effects applied imperatively by threatVisualization ---
  {
    selector: 'node.flagged',
    style: {},
  },
  // --- Edge: A / AAAA records — solid thin blue ---
  {
    selector: 'edge[type = "A"], edge[type = "AAAA"]',
    style: {
      'width': 1.5,
      'line-color': '#58a6ff',
      'line-style': 'solid',
      'target-arrow-shape': 'triangle',
      'target-arrow-color': '#58a6ff',
      'curve-style': 'bezier',
    },
  },
  // --- Edge: NS records — solid thick orange ---
  {
    selector: 'edge[type = "NS"]',
    style: {
      'width': 3,
      'line-color': '#f0883e',
      'line-style': 'solid',
      'target-arrow-shape': 'triangle',
      'target-arrow-color': '#f0883e',
      'curve-style': 'bezier',
    },
  },
  // --- Edge: MX records — dotted green ---
  {
    selector: 'edge[type = "MX"]',
    style: {
      'width': 2,
      'line-color': '#56d364',
      'line-style': 'dotted',
      'target-arrow-shape': 'triangle',
      'target-arrow-color': '#56d364',
      'curve-style': 'bezier',
    },
  },
  // --- Edge: CNAME records — dashed grey ---
  {
    selector: 'edge[type = "CNAME"]',
    style: {
      'width': 1.5,
      'line-color': '#8b949e',
      'line-style': 'dashed',
      'target-arrow-shape': 'vee',
      'target-arrow-color': '#8b949e',
      'curve-style': 'bezier',
    },
  },
];

// ---------------------------------------------------------------------------
// dagre layout options (used for initial load)
// ---------------------------------------------------------------------------

const DAGRE_LAYOUT: cytoscape.LayoutOptions = {
  name: 'dagre',
  rankDir: 'TB',
  nodeSep: 50,
  rankSep: 80,
  padding: 30,
  animate: true,
  animationDuration: 400,
} as cytoscape.LayoutOptions;

// ---------------------------------------------------------------------------
// Hover tooltip type
// ---------------------------------------------------------------------------

interface OverlayTooltip {
  x: number;
  y: number;
  point: ThreatOverlayPoint;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const InfrastructureGraph = forwardRef<InfrastructureGraphHandle>(
  function InfrastructureGraph(_props, ref) {
    const cyRef = useRef<cytoscape.Core | null>(null);
    const elements = useGraphStore((state) => state.elements);
    const setSelectedNode = useGraphStore((state) => state.setSelectedNode);
    const prevElementCountRef = useRef(0);

    const [tooltip, setTooltip] = useState<OverlayTooltip | null>(null);

    // Run dagre layout when element count transitions from 0 -> N (initial load)
    useEffect(() => {
      const cy = cyRef.current;
      if (!cy) return;
      const prev = prevElementCountRef.current;
      const curr = elements.length;
      prevElementCountRef.current = curr;

      if (prev === 0 && curr > 0) {
        cy.layout(DAGRE_LAYOUT).run();
      }
    }, [elements.length]);

    // Wire tap + hover handlers once cy is mounted
    const onCyInit = useCallback(
      (cy: cytoscape.Core) => {
        cyRef.current = cy;

        cy.on('tap', 'node', (evt) => {
          const node = evt.target as cytoscape.NodeSingular;
          const data = node.data() as DNSNode;
          if (data.type !== 'asn') {
            setSelectedNode(data);
          }
        });

        cy.on('tap', (evt) => {
          if (evt.target === cy) {
            setSelectedNode(null);
          }
        });

        // Hover tooltip for flagged nodes
        cy.on('mouseover', 'node.flagged', (evt) => {
          const node = evt.target as cytoscape.NodeSingular;
          const pos = node.renderedPosition();
          const point: ThreatOverlayPoint = {
            target: node.data('label') as string,
            score: (node.data('overlayScore') as number) ?? 0,
            feeds: (node.data('overlayFeeds') as string[]) ?? [],
            label: String(node.data('label')),
            source: (node.data('overlaySource') as string) ?? '',
          };
          setTooltip({ x: pos.x + 16, y: pos.y - 8, point });
        });

        cy.on('mouseout', 'node.flagged', () => {
          setTooltip(null);
        });
      },
      [setSelectedNode],
    );

    // Imperative handle for useScanStream and useOverlayData
    useImperativeHandle(ref, () => ({
      batchAdd: (incoming: CytoElement[]) => {
        const cy = cyRef.current;
        if (!cy) return;
        const toAdd = incoming.filter(
          (el) => cy.getElementById(el.data.id).length === 0,
        );
        if (toAdd.length === 0) return;
        cy.startBatch();
        cy.add(toAdd as unknown as cytoscape.ElementDefinition[]);
        cy.endBatch();
      },
      getCy: () => cyRef.current,
    }));

    return (
      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        <CytoscapeComponent
          elements={elements as unknown as cytoscape.ElementDefinition[]}
          stylesheet={STYLESHEET as unknown as cytoscape.StylesheetStyle[]}
          cy={onCyInit}
          style={{ width: '100%', height: '100%', background: '#0a0e14' }}
          webgl={true}
        />

        {/* Overlay threat tooltip */}
        {tooltip && (
          <div
            style={{
              position: 'absolute',
              left: tooltip.x,
              top: tooltip.y,
              background: '#161b22',
              border: `1px solid ${threatColor(tooltip.point.score)}66`,
              borderRadius: 6,
              padding: '6px 10px',
              pointerEvents: 'none',
              zIndex: 20,
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: 11,
              color: '#c9d1d9',
              minWidth: 140,
              boxShadow: `0 0 12px ${threatColor(tooltip.point.score)}44`,
            }}
          >
            <div style={{ color: threatColor(tooltip.point.score), fontWeight: 700, marginBottom: 2 }}>
              Score {tooltip.point.score}
            </div>
            <div style={{ color: '#8b949e', marginBottom: 4 }}>
              via {tooltip.point.source}
            </div>
            {tooltip.point.feeds.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {tooltip.point.feeds.map((f) => (
                  <span
                    key={f}
                    style={{
                      background: '#ff444422',
                      color: '#ff4444',
                      border: '1px solid #ff444444',
                      borderRadius: 3,
                      padding: '1px 4px',
                    }}
                  >
                    {f}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
);

InfrastructureGraph.displayName = 'InfrastructureGraph';

export { InfrastructureGraph };
